import { useMemo } from 'react';
import sortBy from 'lodash/sortBy';
import { JSONContent } from '@tiptap/core';

import { InteractiveLessonSectionOverview } from 'approot/activity-builder/__generated__/InteractiveLessonSectionOverview';
import { TeachingLearningSequence } from 'approot/activity-builder/components/teaching-notes/__generated__/TeachingLearningSequence';
import { TeachingLearningSequenceRichtextTiptap } from './teaching-learning-sequence-richtext-tiptap';
import { TeachingLearningSequenceViewTitle } from './teaching-learning-sequence.style';
import { getRichtextContent } from './teaching-learning-sequence.utils';
import { getActivityExplanationSequenceItem } from './logic/get-activity-explanation-sequence-item';
import { getActivityPartsSequenceItems } from './logic/get-activity-parts-sequence-items';
import { isRichtextEmpty } from '../richtext/richtext.utils';

type Props = {
  section: InteractiveLessonSectionOverview;
  componentId: string;
  sequences: TeachingLearningSequence[];
  isSmall?: boolean;
};

const TeachingLearningSequenceActivityParts = ({
  section,
  componentId,
  sequences,
  isSmall,
}: Props) => {
  const parts = useMemo(
    () => getActivityPartsSequenceItems(sequences, componentId),
    [sequences, componentId]
  );

  const oldExplanation = useMemo(
    () => getActivityExplanationSequenceItem(sequences, componentId),
    [sequences, componentId]
  );

  const subSectionOverviews = useMemo(
    () => sortBy(section.subSectionsOverview, 'sort'),
    [section.subSectionsOverview]
  );

  const firstPart = useMemo(() => parts.find(a => a.sectionId === section.id), [
    parts,
    section,
  ]);

  const totalParts = useMemo(() => parts.length, [parts]);

  const hasPartNotesData = useMemo(() => {
    return parts.some(part => !isRichtextEmpty(part?.data));
  }, [parts]);

  const hasNotes = useMemo(() => {
    return hasPartNotesData || !isRichtextEmpty(oldExplanation?.data);
  }, [oldExplanation, hasPartNotesData]);

  if (!hasNotes) {
    return null;
  }

  return (
    <>
      <TeachingLearningSequenceViewTitle marginBottom={8}>
        Teaching Notes
      </TeachingLearningSequenceViewTitle>

      {!hasPartNotesData ? (
        <TeachingLearningSequenceRichtextTiptap
          content={getRichtextContent(oldExplanation?.data)}
          isSmall={isSmall}
        />
      ) : (
        <>
          <NoteSection
            title={totalParts > 1 ? `Part 1 of ${totalParts}` : undefined}
            data={firstPart?.data}
            isSmall={isSmall}
          />
          {subSectionOverviews?.map((subSection, index) => {
            const activityDescription = parts.find(
              d => d.sectionId === subSection.id
            );
            return (
              <NoteSection
                key={subSection.id}
                title={`Part ${index + 2} of ${totalParts}`}
                data={activityDescription?.data}
                isSmall={isSmall}
              />
            );
          })}
        </>
      )}
    </>
  );
};

export default TeachingLearningSequenceActivityParts;

const NoteSection = ({
  title,
  isSmall,
  data,
}: {
  title?: string;
  data: JSONContent;
  isSmall?: boolean;
}) => {
  if (isRichtextEmpty(data)) return null;

  return (
    <>
      {title && (
        <TeachingLearningSequenceViewTitle as="h5" marginBottom={4}>
          {title}
        </TeachingLearningSequenceViewTitle>
      )}

      <TeachingLearningSequenceRichtextTiptap
        content={getRichtextContent(data)}
        isSmall={isSmall}
      />
    </>
  );
};
